/* eslint-disable max-lines -- the rule was disabled before a comment became required */
import {
  type PimProductV11,
  type PimProductWithVariantsV1,
  type PimVariantAttributeV1,
} from '@redteclab/api/clients/bully'

import { ATTRIBUTE_NAME_CONTACT_LENSES } from './ATTRIBUTE_NAME'
import {
  type AttributeGroupData,
  type AttributeGroups,
  type SelectionEntry,
} from './AttributeGroup'
import { sortSelectionEntriesInGroups } from './sortSelectionEntriesInGroups'

type AttributesWithVariants = {
  attribute: PimVariantAttributeV1
  variantsHavingThisAttribute: PimProductV11[]
}

export const ATTRIBUTE_VALUE_PACKAGING_VARIANT_SET = 'Set'

/**
 * collects all unique attributes from all the variants of PimProductWithVariantsV1.
 * adds all the variants that have this specific attribute.
 * returns tuples of [{attribute, variantsHavingThisAttribute}]
 * uniqueness defined by variant_attribute and value combination.
 */
const collectUniqueAttributesByNameAndValue = (
  product: PimProductWithVariantsV1,
): AttributesWithVariants[] => {
  const { variants } = product
  const uniqueMap: Record<string, AttributesWithVariants> = {}

  for (const variant of variants) {
    if (variant.variant_attributes) {
      for (const attribute of variant.variant_attributes) {
        const key = `${attribute.variant_attribute}${attribute.value ?? ''}`
        const existing = uniqueMap[key]

        // if this attribute was met before, add this variant to the list of variants that have this attribute
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- complicates more if type would contain optionals
        if (existing) {
          existing.variantsHavingThisAttribute.push(variant)

          // eslint-disable-next-line no-continue -- don't reference, refactor if touched
          continue
        }

        // no previous case, create a new entry
        uniqueMap[key] = {
          attribute,
          variantsHavingThisAttribute: [variant],
        }
      }
    }
  }

  return Object.values(uniqueMap)
}

/**
 * takes a list of attributes and groups them by attribute name.
 * input:
 * [
 *   {variant_attribute: 'color', value: 'red'},
 *   {variant_attribute: 'size', value: 'm'},
 *   {variant_attribute: 'color', value: 'black'},
 * ]
 * output:
 * [
 *   'color': [
 *     {isSelected: false, ..., attribute: {variant_attribute: 'color', value: 'red'}},
 *     {isSelected: false, ..., attribute: {variant_attribute: 'color', value: 'black'}},
 *   ],
 *   'size': [
 *     {isSelected: false, ..., attribute: {variant_attribute: 'size', value: 'm'}},
 *   ]
 * ]
 */
const buildUnmarkedGroups = (
  uniqueAttributes: AttributesWithVariants[],
): AttributeGroups =>
  // eslint-disable-next-line unicorn/no-array-reduce -- the rule was disabled before a comment became required
  uniqueAttributes.reduce(
    (acc: AttributeGroups, { attribute, variantsHavingThisAttribute }) => {
      const data: AttributeGroupData = acc[attribute.variant_attribute] ?? {
        complete: Boolean(attribute.complete),
        metaAttributeData: { ...attribute },
        selectionEntries: [],
        shouldDisplayOnlySelectedValue: false,
        shouldRenderGroup: true,
        shouldShowPricesOnSelections: false,
      }

      data.selectionEntries.push({
        allCandidateVariants: [],
        allowDeselection: true,
        attribute: { ...attribute },
        dontSelectCandidateOnClick: false,
        edgecaseSetAttributesOfCandidateAsSelectedOnSelection: false,
        isSelected: false,
        variantsHavingThisAttribute,
      })

      acc[attribute.variant_attribute] = data

      return acc
    },
    {},
  )

/**
 * marks isSelected true for attributes that are selected.
 */
const markSelectedAttributes = (
  selectionGroups: AttributeGroups,
  selectedAttributes: PimVariantAttributeV1[],
): AttributeGroups => {
  for (const selectedAttribute of selectedAttributes) {
    const group = selectionGroups[selectedAttribute.variant_attribute]

    if (!group) {
      // eslint-disable-next-line no-continue -- don't reference, refactor if touched
      continue
    }

    const attributeToMark = group.selectionEntries.find(
      (selectionEntry) =>
        selectionEntry.attribute.value === selectedAttribute.value,
    )

    if (attributeToMark) {
      attributeToMark.isSelected = true
    }
  }

  return selectionGroups
}

/**
 * checks if both attributes match by equality of specific properties
 */
const areAttributesMatching = (
  attributeA: PimVariantAttributeV1,
  attributeB: PimVariantAttributeV1,
): boolean => {
  return (
    attributeA.variant_attribute === attributeB.variant_attribute &&
    attributeA.value === attributeB.value
  )
}
/**
 * returns variants that have all the exact specified attributes.
 */
export const filterVariantsMatchingAttributes = (
  variants: PimProductV11[],
  attributesToMatch: PimVariantAttributeV1[],
): PimProductV11[] => {
  if (attributesToMatch.length === 0) {
    return variants
  }

  return variants.filter((variant) => {
    return attributesToMatch.every((attributeToMatch) => {
      return variant.variant_attributes?.find((attribute) => {
        return areAttributesMatching(attribute, attributeToMatch)
      })
    })
  })
}

/**
 * it may be the case that some attribute groups are not selected on UI level.
 * this returns a list of those attribute groups
 */
const getNotSelectedGroups = (
  attributeGroups: AttributeGroups,
  selectedAttributes: PimVariantAttributeV1[],
): AttributeGroupData[] => {
  const notSelectedGroups: AttributeGroupData[] = []

  for (const group of Object.values(attributeGroups)) {
    if (!group) {
      // eslint-disable-next-line no-continue -- don't reference, refactor if touched
      continue
    }

    const isSelected = selectedAttributes.some(
      (selectedAttribute) =>
        selectedAttribute.variant_attribute ===
        group.metaAttributeData.variant_attribute,
    )

    if (!isSelected) {
      notSelectedGroups.push(group)
    }
  }

  return notSelectedGroups
}

/**
 * takes selected attributes, and selection groups.
 * for each selected attribute group, takes rest selected attributes and finds all the variants that match all the rest selected attributes
 * and one of the current group.
 *
 * example logic:
 * all attributes: [{size: 'm', color: 'red'}, {size: 'l', color: 'red'}, {size: 'l', color: 'black'}]
 * variants: ['m', 'red'] , ['l', 'red'], ['l', 'black']
 * currentlySelected: ['m', 'red']
 *
 * to check if size l has variants if be selected:
 * we take all the variants that match the selected color 'red' - ['l', 'red'], ['m', 'red']
 * check if any of those variants as well has size l - ['l', 'red'] was found
 * because we have variant 'l', 'red' we add to l a candidate variant.
 *
 * to check if color black has variants if be selected:
 * we find all the variants that match the selected size 'm' - ['m', 'red']
 * we search for if found as well have color 'black' - not found
 * no variants found that match both size 'm' and color 'black' - no candidate variant.
 *
 * For more than 2 type attributes case, same logic applied for each attribute type.
 * For each take current and rest, find variants matching rest, filter that have current as well.
 */
const matchAndSetCandidateVariants = (
  attributeGroupsMap: AttributeGroups,
  attributesToMatch: PimVariantAttributeV1[],
  variants: PimProductV11[],
): AttributeGroups => {
  for (const [name, group] of Object.entries(attributeGroupsMap)) {
    if (!group) {
      // eslint-disable-next-line no-continue -- don't reference, refactor if touched
      continue
    }

    const restSelected = attributesToMatch.filter((it) => {
      return name !== it.variant_attribute
    })

    for (const selectionEntry of group.selectionEntries) {
      selectionEntry.allCandidateVariants = filterVariantsMatchingAttributes(
        variants,
        [...restSelected, selectionEntry.attribute],
      )
    }
  }

  return attributeGroupsMap
}

/**
 * For edgecases - when some selection entries have more than one candidate,
 * create a same selection entry for each candate.
 */
const duplicateSelectionEntriesSoTheyHaveOneCandidate = (
  selectionEntries: SelectionEntry[],
  currentVariant: PimProductV11,
): SelectionEntry[] => {
  // eslint-disable-next-line unicorn/no-array-reduce -- the rule was disabled before a comment became required
  return selectionEntries.reduce(
    (acc: SelectionEntry[], originalSelectionEntry) => {
      const hasMoreThanOneCandidate =
        originalSelectionEntry.allCandidateVariants.length > 1

      if (!hasMoreThanOneCandidate) {
        acc.push(originalSelectionEntry)

        return acc
      }

      for (const candidateVariant of originalSelectionEntry.allCandidateVariants) {
        acc.push({
          ...originalSelectionEntry,
          allCandidateVariants: [candidateVariant],
          isSelected:
            originalSelectionEntry.isSelected &&
            currentVariant.upid === candidateVariant.upid,
        })
      }

      return acc
    },
    [],
  )
}

/** visual condition: if no selections shall be shown, but only the single selected value */
const getShouldDisplayOnlySelectedValue = (
  group: AttributeGroupData,
): boolean => {
  const selected = group.selectionEntries.some((it) => it.isSelected)
  const moreThanOne = group.selectionEntries.length > 1

  return (selected && !moreThanOne) || !group.complete
}

/**
 * if attribute has one candidate clicking leads to the selection of this candidate,
 * this flag specifies that only the attribute shall be selected
 */
const getDontSelectCandidateOnClick = (
  selectionEntry: SelectionEntry,
  unselectedAttributes: AttributeGroupData[],
  doesContainOnlyOneCompleteGroup: boolean,
): boolean => {
  if (doesContainOnlyOneCompleteGroup) {
    return false
  }
  /**
   * only one unselected group, if entry of this group can select variant
   * other groups just selectabel without variant change
   */
  if (unselectedAttributes.length === 1) {
    return (
      selectionEntry.attribute.variant_attribute !==
      unselectedAttributes[0].metaAttributeData.variant_attribute
    )
  }

  /**
   * if more than one unselected group, any selection entry should not select variant
   */
  if (
    unselectedAttributes.length > 1 &&
    selectionEntry.allCandidateVariants.length > 0
  ) {
    return true
  }

  return selectionEntry.allCandidateVariants.length > 1
}

/**
 * sorts groups by passed comparator function.
 * @example
 * sortGroups(groups, (a, b) => a.metaAttributeData.size - b.metaAttributeData.size)
 */
const sortGroups = (
  groups: AttributeGroups,
  compareFunc: (
    aGroup: AttributeGroupData,
    bGroup: AttributeGroupData,
  ) => number,
): AttributeGroups => {
  const sortedGroups = Object.values(groups).toSorted((aGroup, bGroup) => {
    if (!aGroup || !bGroup) {
      return 0
    }

    return compareFunc(aGroup, bGroup)
  })

  // eslint-disable-next-line unicorn/no-array-reduce -- the rule was disabled before a comment became required
  return sortedGroups.reduce<AttributeGroups>((acc, group) => {
    if (!group) {
      return acc
    }

    acc[group.metaAttributeData.variant_attribute] = group

    return acc
  }, {})
}

const containsOnlyOneCompleteGroup = (groups: AttributeGroups): boolean => {
  let completeCount = 0
  for (const group of Object.values(groups)) {
    if (completeCount > 1) {
      return false
    }
    if (group?.complete) {
      completeCount += 1
    }
  }

  return completeCount === 1
}

const getCompleteGroupOrThrow = (
  groups: AttributeGroups,
): AttributeGroupData => {
  for (const group of Object.values(groups)) {
    if (group?.complete) {
      return group
    }
  }

  throw new Error('no completed group')
}

const sortStandardGroups = (groups: AttributeGroups): AttributeGroups => {
  const onlyOneComplete = containsOnlyOneCompleteGroup(groups)
    ? getCompleteGroupOrThrow(groups)
    : null

  return sortGroups(groups, (aGroup, bGroup) => {
    const aGroupIsOnlyOneComplete = onlyOneComplete === aGroup ? 1 : 0
    const bGroupIsOnlyOneComplete = onlyOneComplete === bGroup ? 1 : 0
    /**
     * case when only one group shall be shown with possible selections.
     */
    if (aGroupIsOnlyOneComplete || bGroupIsOnlyOneComplete) {
      return aGroupIsOnlyOneComplete - bGroupIsOnlyOneComplete
    }

    const aComplete = aGroup.complete ? 1 : 0
    const bComplete = bGroup.complete ? 1 : 0

    // contact lenses go to end
    const aGroupIsContactLenses =
      aGroup.metaAttributeData.group === ATTRIBUTE_NAME_CONTACT_LENSES ? 1 : 0
    const bGroupIsContactLenses =
      bGroup.metaAttributeData.group === ATTRIBUTE_NAME_CONTACT_LENSES ? 1 : 0
    if (aGroupIsContactLenses && bGroupIsContactLenses) {
      /**
       * contacted lenses will be presented as "separate group", within that group,
       * if both are contact lenses, ones with single selection entry go top.
       * groups that incomplete (e.g. have only one possible attribute) - are shown on top.
       */
      return aComplete - bComplete
    }

    /**
     * contact lenses are always in the end.
     */
    if (aGroupIsContactLenses || bGroupIsContactLenses) {
      return aGroupIsContactLenses - bGroupIsContactLenses
    }

    if (aComplete || bComplete) {
      return aComplete - bComplete
    }

    return 0
  })
}

const shouldRenderGroup = (
  group: AttributeGroupData,
  currentVariant?: PimProductV11,
): boolean => {
  if (!currentVariant) {
    return true
  }
  const variantsAttributes =
    currentVariant.variant_attributes?.map((it) => it.variant_attribute) ?? []

  return variantsAttributes.includes(group.metaAttributeData.variant_attribute)
}

const getShouldShowPricesOnSelections = (
  doesContainOnlyOneCompleteGroup: boolean,
  group: AttributeGroupData,
): boolean => {
  if (group.metaAttributeData.type === 'LIST') {
    return false
  }

  return doesContainOnlyOneCompleteGroup && group.complete
}

const markVisualState = (
  groups: AttributeGroups,
  selectedAttributes: PimVariantAttributeV1[],
  currentVariant?: PimProductV11,
): AttributeGroups => {
  const unselectedAttributes = getNotSelectedGroups(groups, selectedAttributes)

  const doesContainOnlyOneCompleteGroup = containsOnlyOneCompleteGroup(groups)

  for (const group of Object.values(groups)) {
    if (!group) {
      // eslint-disable-next-line no-continue -- don't reference, refactor if touched
      continue
    }
    group.shouldDisplayOnlySelectedValue =
      getShouldDisplayOnlySelectedValue(group)
    group.shouldRenderGroup = shouldRenderGroup(group, currentVariant)
    for (const selectionEntry of group.selectionEntries) {
      selectionEntry.dontSelectCandidateOnClick = getDontSelectCandidateOnClick(
        selectionEntry,
        unselectedAttributes,
        doesContainOnlyOneCompleteGroup,
      )
      selectionEntry.allowDeselection = !doesContainOnlyOneCompleteGroup
    }
    group.shouldShowPricesOnSelections = getShouldShowPricesOnSelections(
      doesContainOnlyOneCompleteGroup,
      group,
    )
  }

  return groups
}

/**
 * EDGE-CASE: backend data may contain invalid state:
 * in case of conflicting candidates (e.g. 2 variants with [s, 1pc, black], [s, 1pc, black])
 * we leave only first variant, so only one is left
 */
const ensureHasOnlyOneCandidateEdgeCase = (
  groups: AttributeGroups,
): AttributeGroups => {
  for (const group of Object.values(groups)) {
    if (!group) {
      // eslint-disable-next-line no-continue -- don't reference, refactor if touched
      continue
    }
    for (const selectionEntry of group.selectionEntries) {
      if (selectionEntry.allCandidateVariants.length > 1) {
        selectionEntry.allCandidateVariants = [
          selectionEntry.allCandidateVariants[0],
        ]
      }
    }
  }

  return groups
}

const handleEdgeCaseOnlyOneCompleteGroup = ({
  currentVariant,
  groups,
  pimProduct,
  selectedAttributes,
}: {
  currentVariant: PimProductV11
  groups: AttributeGroups
  pimProduct: PimProductWithVariantsV1
  selectedAttributes: PimVariantAttributeV1[]
}): AttributeGroups => {
  const completeGroup = getCompleteGroupOrThrow(groups)
  const onlyCompleteGroupAsSelected = selectedAttributes.filter(
    (it) =>
      it.variant_attribute ===
      completeGroup.metaAttributeData.variant_attribute,
  )
  const updatedGroups = matchAndSetCandidateVariants(
    groups,
    onlyCompleteGroupAsSelected,
    pimProduct.variants,
  )
  completeGroup.selectionEntries =
    duplicateSelectionEntriesSoTheyHaveOneCandidate(
      completeGroup.selectionEntries,
      currentVariant,
    )

  for (const it of completeGroup.selectionEntries) {
    it.edgecaseSetAttributesOfCandidateAsSelectedOnSelection = true
  }

  return updatedGroups
}

const prepareGroups = (
  allAttributes: AttributesWithVariants[],
  selectedAttributes: PimVariantAttributeV1[],
): AttributeGroups => {
  // build a map, so it can be easier to worked on.
  let groups = buildUnmarkedGroups(allAttributes)

  groups = sortStandardGroups(groups)

  groups = markSelectedAttributes(groups, selectedAttributes)

  return groups
}

export const calcProductPageAttributesSelectionState = (
  pimProduct: PimProductWithVariantsV1,
  selectedAttributes: PimVariantAttributeV1[],
  currentVariant: PimProductV11,
): AttributeGroups => {
  // collect all unique attributes without duplicates (all attributes that be rendered)
  const allAttributes = collectUniqueAttributesByNameAndValue(pimProduct)

  let groups = prepareGroups(allAttributes, selectedAttributes)

  const doesContainOnlyOneCompleteGroup = containsOnlyOneCompleteGroup(groups)

  if (doesContainOnlyOneCompleteGroup) {
    groups = handleEdgeCaseOnlyOneCompleteGroup({
      currentVariant,
      groups,
      pimProduct,
      selectedAttributes,
    })
  } else {
    groups = matchAndSetCandidateVariants(
      groups,
      selectedAttributes,
      pimProduct.variants,
    )
    groups = ensureHasOnlyOneCandidateEdgeCase(groups)
  }

  groups = markVisualState(groups, selectedAttributes, currentVariant)

  groups = sortSelectionEntriesInGroups(groups)

  return groups
}
